import { Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useWishlist } from '@/hooks/useWishlist';
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';

interface WishlistButtonProps {
  productId: string | number; // Accept both for flexibility, will be normalized internally
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  onClick?: (e: React.MouseEvent) => void;
}

export const WishlistButton = ({
  productId,
  className = '',
  variant = 'ghost',
  onClick
}: WishlistButtonProps) => {
  const { user } = useAuth();
  const { addToWishlist, isInWishlist } = useWishlist();
  const { toast } = useToast();
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      isInWishlist(productId).then(setInWishlist);
    }
  }, [productId, user, isInWishlist]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Call parent onClick if provided
    if (onClick) {
      onClick(e);
    }

    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to add items to your wishlist",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const added = await addToWishlist(productId);
      setInWishlist(added);
      toast({
        title: added ? "Added to Wishlist" : "Removed from Wishlist",
        description: added
          ? "Item saved to your wishlist"
          : "Item removed from your wishlist",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update wishlist",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size="icon"
      onClick={handleClick}
      disabled={loading}
      className={`${className} hover:text-red-500 transition-colors ${
        variant === 'ghost'
          ? 'h-8 w-8 rounded-full bg-card/90 backdrop-blur-sm hover:bg-card'
          : ''
      }`}
    >
      <Heart
        className={`h-4 w-4 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
      />
    </Button>
  );
};
